import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { stats } from '@/mocks/faq';

export default function AboutPage() {
  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - hth官方平台',
    description: '了解hth品牌故事、信誉保障与联系方式。hth致力于提供正品保障、极速响应与全方位服务支持。',
    url: 'https://www.hth-platform.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: 'hth官方平台',
      url: 'https://www.hth-platform.com/',
    },
    about: {
      '@type': 'Organization',
      name: 'hth官方平台',
      description: '正品保障与极速服务的首选平台',
      url: 'https://www.hth-platform.com/',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'hth官方平台',
    url: 'https://www.hth-platform.com/',
    description: 'hth是您信赖的首选平台，提供正品保障、极速响应与全方位服务支持。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-XXX-XXXX',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    sameAs: [
      'https://www.weibo.com/hth',
      'https://www.weixin.qq.com/hth',
    ],
  };

  const milestones = [
    { year: '2020', event: 'hth平台正式成立，开启品质服务之旅' },
    { year: '2021', event: '用户突破百万，推出会员等级体系' },
    { year: '2022', event: '正品保障体系全面建立，获行业认证' },
    { year: '2023', event: '用户突破五百万，极速服务体系上线' },
    { year: '2024', event: '技术架构全面升级，推出全球配送服务' },
    { year: '2025', event: '用户突破千万，荣获年度最佳客户体验奖' },
    { year: '2026', event: '持续创新，为用户创造更多价值' },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: '正品为本',
      desc: '我们坚持从源头把控品质，只提供经过严格验证的正品商品，让每位用户买得放心。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '用户至上',
      desc: '用户的满意是我们最大的追求。我们不断优化服务流程，提升响应速度，用心服务每一位用户。',
    },
    {
      icon: 'ri-lightbulb-line',
      title: '持续创新',
      desc: '我们持续引入先进技术与创新理念，不断优化平台体验，为用户创造更大价值。',
    },
    {
      icon: 'ri-team-line',
      title: '诚信共赢',
      desc: '以诚信为基石，与合作伙伴、用户建立长期稳定的信任关系，实现多方共赢。',
    },
  ];

  const reputations = [
    { icon: 'ri-medal-line', title: '行业认证', desc: '通过多项行业权威认证' },
    { icon: 'ri-star-line', title: '用户好评', desc: '用户满意度持续领先' },
    { icon: 'ri-award-line', title: '最佳体验奖', desc: '年度最佳客户体验奖' },
    { icon: 'ri-safe-2-line', title: '安全认证', desc: '银行级数据安全认证' },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <ScrollToTop />
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          {/* Header */}
          <div className="bg-background-100">
            <div className="w-full px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-4xl mx-auto">
                <div className="mb-4">
                  <Breadcrumb
                    items={[
                      { label: '首页', path: '/' },
                      { label: '关于我们' },
                    ]}
                  />
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                  关于我们
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl">
                  了解hth的品牌故事、核心价值观与信誉保障
                </p>
              </div>
            </div>
          </div>

          {/* Brand Intro */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                    品牌介绍
                  </h2>
                  <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <strong>hth官方平台</strong>成立于2020年，致力于为用户提供正品保障、极速响应与全方位服务支持的品质平台。我们秉持"正品为本、用户至上"的核心理念，从供应链源头到用户手中，每一个环节都精益求精。
                  </p>
                  <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                    截至目前，hth已累计服务超过<strong>1000万用户</strong>，正品率高达<strong>99.99%</strong>，客服平均响应时间仅<strong>30秒</strong>。这些数字背后，是hth团队对品质服务的不懈追求。
                  </p>
                </div>
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20elegant%20modern%20corporate%20office%20with%20soft%20natural%20lighting%2C%20minimalist%20interior%20design%2C%20cream%20and%20golden%20accents%2C%20professional%20trustworthy%20atmosphere%2C%20team%20collaboration%20scene%2C%20high-end%20aesthetic&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                    alt="hth品牌办公环境"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
                      {stat.value}
                    </div>
                    <div className="mt-1.5 text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
                核心价值观
              </h2>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {values.map((v) => (
                  <div
                    key={v.title}
                    className="p-6 rounded-lg border border-background-200 bg-background-50"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-50 text-accent-500">
                      <i className={`${v.icon} text-lg`}></i>
                    </div>
                    <h3 className="mt-3 text-base font-heading font-semibold text-foreground-900">
                      {v.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
                发展历程
              </h2>
              <div className="mt-10 relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((m, index) => (
                    <div
                      key={m.year}
                      className={`relative flex items-start gap-6 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-100"></div>
                      <div className={`ml-10 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                        <span className="text-sm font-bold text-primary-500">{m.year}</span>
                        <p className="mt-1 text-sm text-foreground-600">{m.event}</p>
                      </div>
                      {index % 2 === 0 ? <div className="hidden md:block md:w-1/2"></div> : <div className="hidden md:block md:w-1/2"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section id="reputation" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
                信誉展示
              </h2>
              <p className="mt-2 text-sm text-foreground-500 text-center">
                hth获得的行业认可与荣誉资质
              </p>
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                {reputations.map((r) => (
                  <div
                    key={r.title}
                    className="p-5 rounded-lg border border-background-200 bg-background-50 text-center"
                  >
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                      <i className={`${r.icon} text-xl`}></i>
                    </div>
                    <h4 className="mt-3 text-sm font-heading font-semibold text-foreground-900">
                      {r.title}
                    </h4>
                    <p className="mt-1 text-xs text-foreground-500">{r.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-500 text-center">
                如有任何问题，欢迎随时与我们取得联系
              </p>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="p-6 rounded-lg border border-background-200 bg-background-50 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h4 className="mt-4 text-sm font-heading font-semibold text-foreground-900">
                    客服热线
                  </h4>
                  <p className="mt-1 text-sm text-foreground-500">400-XXX-XXXX</p>
                  <p className="text-xs text-foreground-400">工作日 9:00 - 18:00</p>
                </div>

                <div className="p-6 rounded-lg border border-background-200 bg-background-50 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h4 className="mt-4 text-sm font-heading font-semibold text-foreground-900">
                    电子邮箱
                  </h4>
                  <p className="mt-1 text-sm text-foreground-500">support@hth-platform.com</p>
                  <p className="text-xs text-foreground-400">7×24小时接收邮件</p>
                </div>

                <div className="p-6 rounded-lg border border-background-200 bg-background-50 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-wechat-line text-xl"></i>
                  </div>
                  <h4 className="mt-4 text-sm font-heading font-semibold text-foreground-900">
                    微信公众号
                  </h4>
                  <p className="mt-1 text-sm text-foreground-500">hth官方平台</p>
                  <p className="text-xs text-foreground-400">关注获取最新资讯</p>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}